import React from 'react';
import {connect} from 'react-redux';
import isEmpty from 'is-empty';
import {Card, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource, resetResource} from 'spectra-logic-ui/actions';
import {Color, SpectraLogoColor} from 'spectra-logic-ui/colors';
import {Table, Tooltip} from 'spectra-logic-ui/components';
import {dateTimeLong} from 'spectra-logic-ui/helpers/date';

import {storageClassString} from '@/enum';
import {getTitle} from '@/title';
import {Destination, Pool} from '@/types';
import ButtonToolbar from '@/components/button_toolbar';
import {Object, ObjectStorageState} from '@/buckets/objects/types';
import AddStorageDialog from '@/buckets/objects/details/add_storage_dialog';
import DeleteStorageDialog from '@/buckets/objects/details/delete_storage_dialog';
import VerifyObjectDialog from '@/buckets/objects/details/verify_index';

type Props = {
  bucketName: string;
  object: Object;
  objectStorage?: ObjectStorageState;
  destinations?: Destination[];
  pools?: Pool[];
  fetching?: boolean;
  error?: boolean;
  fetchDestinations?: () => Promise<any>;
  fetchObjectStorage?: () => Promise<any>;
  fetchPools?: () => Promise<any>;
  fetchObjects?: Function;
  resetStorage?: any;
}

const useStyles = makeStyles({
  tableCell: {
    color: Color.WHITE,
  },
  card: {
    margin: 20,
  },
  row: {
    background: SpectraLogoColor.BLUE,
    height: 47,
  },
  root: {
    height: '18px !important',
    color: `${Color.WHITE} !important`,
  },
  noStorage: {
    margin: '10px',
  },
});

const noStorageMessage = 'This object does not require any storage';
const noStorageTooltip = (title: string) => {
  return 'Small objects are stored directly in the database alongside object metadata, and thus ' +
  'require no storage. Object content for these objects is generally smaller in size than the metadata required ' +
  'by ' + title + ' to manage the object.  It is therefore more efficient for the system to store small objects ' +
  'in the database and not push them out to storage. The third-party recovery option can result in storage being ' +
  'added even though it\'s not otherwise required';
};

const StorageDetails = (props: Props) => {
  const {fetching = false, error = false, fetchDestinations, bucketName, object, pools = [] as Pool[],
    destinations = [] as Destination[], objectStorage = {} as ObjectStorageState, fetchObjectStorage, fetchObjects,
    fetchPools, resetStorage} = props;
  const classes = useStyles();

  const [selectedStorageID, setSelectedStorageID] = React.useState('');

  React.useEffect(() => {
    if (fetchDestinations !== undefined) {
      fetchDestinations();
    }
    if (fetchPools !== undefined) {
      fetchPools();
    }
    setSelectedStorageID('');
    if (fetchObjectStorage !== undefined) {
      fetchObjectStorage();
    }
    return () => {
      if (resetStorage !== undefined) {
        resetStorage();
      }
    };
  }, [object.key, object.versionId]);

  const destNameMap = {} as any;
  const destClassMap = {} as any;
  pools.forEach((pool) => {
    destNameMap[pool.id] = pool.name;
    destClassMap[pool.id] = storageClassString(pool.storageClass);
  });
  const mergedDests = objectStorage.storage && JSON.parse(JSON.stringify(objectStorage.storage));
  mergedDests?.forEach((destination:any, i: any) => {
    mergedDests[i]['name'] = destNameMap[destination.id];
  });
  mergedDests?.sort((a:any, b:any) => (a.name > b.name) ? 1 : -1);

  const attributesToolTip =
    <div>
      <div>Active: The transfer is in progress.</div>
      <div>Archived: Data must be restored to access it.</div>
      <div>Incompatible: This storage cannot be added.</div>
      <div>Pending: The transfer is pending.</div>
      <div>Restored: Archived data has been restored.</div>
      <div>Retry: The transfer did not complete on the first attempt.</div>
      <div>Secondary: This cannot be the object's only storage.</div>
    </div>;

  return (
    <div>
      <Table>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Lifecycle Status
              <Tooltip>
                {(!objectStorage.scheduled && !objectStorage.processing) && 'No lifecycle changes scheduled.' ||
                objectStorage.restoring && 'Restore in progress.' ||
                objectStorage.processing && 'Lifecycle changes in progress.' ||
                objectStorage.scheduled && 'Lifecycle changes are scheduled.'}
              </Tooltip>
            </Table.Cell>
            <Table.Cell>
              {!objectStorage.scheduled ? 'None' :
                objectStorage.processing ? `Active since: ${dateTimeLong(objectStorage.scheduled)}` :
                  `Scheduled at: ${dateTimeLong(objectStorage.scheduled)}`}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      {objectStorage.storageNotNeeded &&
        <Typography className={classes.noStorage}>
          {noStorageMessage}<Tooltip>{noStorageTooltip(getTitle())}</Tooltip>
        </Typography>}
      <Card className={classes.card}>
        <ButtonToolbar>
          <ButtonToolbar.CreateDialogButton dialog={(props: any) => (
            <AddStorageDialog
              bucketName={bucketName}
              object={object}
              destinations={destinations || []}
              {...props}
              onSuccess={() => {
                if (fetchObjectStorage) {
                  fetchObjectStorage();
                }
              }}
            />
          )}/>
          <ButtonToolbar.VerifyDialogButton
            disabled={isEmpty(selectedStorageID)} dialog={(props: any) => (
              <VerifyObjectDialog
                id={selectedStorageID}
                name={destNameMap[selectedStorageID]}
                bucketName={bucketName}
                object={object}
                {...props}
                onSuccess={() => {
                  if (fetchObjectStorage) {
                    fetchObjectStorage();
                  }
                  if (fetchObjects) {
                    // Need to refresh the properties tab and refetching the list of objects
                    // is the only way we have to do that at the moment.
                    fetchObjects();
                  }
                }}
              />
            )}
          />
          <ButtonToolbar.DeleteDialogButton
            disabled={isEmpty(selectedStorageID)} dialog={(props: any) => (
              <DeleteStorageDialog
                id={selectedStorageID}
                name={destNameMap[selectedStorageID]}
                bucketName={bucketName}
                object={object}
                {...props}
                onSuccess={() => {
                  if (fetchObjectStorage) {
                    fetchObjectStorage();
                  }
                  if (fetchObjects) {
                    // Need to refresh the properties tab and refetching the list of objects
                    // is the only way we have to do that at the moment.
                    fetchObjects();
                  }
                }}
              />
            )}
          />
        </ButtonToolbar>
        <Table size='small'>
          <Table.Header >
            <Table.Row className={classes.row}>
              <Table.Cell className={classes.tableCell}>
                <Typography>Storage</Typography>
              </Table.Cell>
              <Table.Cell className={classes.tableCell}>
                <div style={{display: 'flex'}}>
                  <Typography>Attributes</Typography>
                  <Tooltip className={classes.root}>{attributesToolTip}</Tooltip>
                </div>
              </Table.Cell>
              <Table.Cell className={classes.tableCell}>
                <Typography>Storage Class</Typography>
              </Table.Cell>
              <Table.Cell className={classes.tableCell}>
                <Typography>Optional</Typography>
              </Table.Cell>
            </Table.Row>
          </Table.Header>
          <Table.Body hasError={error} isLoading={fetching} colSpan={4}>
            {(mergedDests || []).map((storageCopy: any) => {
              return (
                <Table.Row
                  key={storageCopy.id} selected={!isEmpty(selectedStorageID) && storageCopy.id === selectedStorageID}
                  onClick={() => setSelectedStorageID(storageCopy.id)}
                >
                  <Table.Cell>
                    {storageCopy.name ? (`${storageCopy.name} (${storageCopy.id})`) : storageCopy.id}
                  </Table.Cell>
                  <Table.Cell>
                    {storageCopy.active ? 'active' : ''}
                    {storageCopy.archived ? 'archived' : ''}
                    {storageCopy.pending ? storageCopy.active ? '' : ' pending' : ''}
                    {storageCopy.restored ? ' restored' : ''}
                    {storageCopy.retry ? ' retry' : ''}
                    {storageCopy.secondary ? ' secondary' : ''}
                    {storageCopy.incompatible ? ' incompatible' : ''}
                  </Table.Cell>
                  <Table.Cell>{destClassMap[storageCopy.id] || ''}</Table.Cell>
                  <Table.Cell>{storageCopy.optional ? 'Yes' : 'No'}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Card>
    </div>
  );
};

const mapStateToProps = (state: Store, {object, bucketName}: Props) => {
  const objectStorageUrl = `buckets/${bucketName}/storage/list/${object.key}`;
  const objectStorageResource = state.resources[objectStorageUrl] || {};
  const destinations = state.resources.destinations || {};
  const pools = state.resources.storage || {};
  return {
    objectStorage: objectStorageResource.data,
    error: objectStorageResource.error,
    fetching: objectStorageResource.fetching,
    destinations: destinations.data || [],
    pools: pools.data || [],
  };
};

const mapDispatchToProps = (dispatch: Dispatch, {object, bucketName}: Props) => {
  const objectStorageUrl = `buckets/${bucketName}/storage/list/${object.key}`;
  const query = {
    version: object.versionId !== undefined ? object.versionId : '',
  };
  return {
    fetchObjectStorage: () => dispatch(fetchResource(objectStorageUrl, '', {query: query})),
    resetStorage: () => dispatch(resetResource(objectStorageUrl)),
    fetchDestinations: () => dispatch(fetchResource('destinations')),
    fetchPools: () => dispatch(fetchResource('storage')),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(StorageDetails);
